import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Grid, Tooltip, Typography } from "@mui/material";
import React from "react";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import { IRootReducer } from "../../../reducers";
import { NotifMediums } from "../../../enums/notif-types";
import { getAllNotificationGroupsRequest, getUserNotificationPrefsRequest, updateUserNotificationPrefsRequest } from "../../../actions";
import { NotificationGroup } from "../../../reducers/states/user-info";
import { ParsePhoneNumberUtil } from "../../../utils/string-utils";

const EditNotificationPreferences: FC = () => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [userNotifGroups, setUserNotifGroups] = useState([] as number[])
  const [userIsTextable, setIsTextable] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const userInfoStore = useSelector((state: IRootReducer) => state.userInfoReducer);

  useEffect(() => {
    if (userInfoStore.userNotificationPrefs.groupIds) {
      setUserNotifGroups(userInfoStore.userNotificationPrefs.groupIds)
    }
  }, [userInfoStore.userNotificationPrefs.groupIds]);


  useEffect(() => {
    setIsTextable(!!userInfoStore.userNotificationPrefs.isTextable);
  }, [userInfoStore.userNotificationPrefs.isTextable]);

  useEffect(() => {
    if (userInfoStore.userNotificationPrefs) {
      const isUnchanged =
        userIsTextable === userInfoStore.userNotificationPrefs.isTextable
        && userNotifGroups.length === userInfoStore.userNotificationPrefs.groupIds?.length
        && userNotifGroups.every(id => userInfoStore.userNotificationPrefs.groupIds?.indexOf(id) > -1);

      setSaveDisabled(isUnchanged);
    }
  }, [userIsTextable, userInfoStore.userNotificationPrefs, userNotifGroups])

  const handleOpen = () => {

    dispatch(getUserNotificationPrefsRequest(userInfoStore.userInfo.email ? userInfoStore.userInfo.email : ""));

    if (!userInfoStore.allNotificationGroups.length) {
      dispatch(getAllNotificationGroupsRequest());
    }

    setOpen(true);
  };

  const handleNotifGroupChange = (event: ChangeEvent<HTMLInputElement>) => {
    const groupId = Number(event.target.id);
    const newGroups = new Set(userNotifGroups);
    event.target.checked ? newGroups.add(groupId) : newGroups.delete(groupId);

    setUserNotifGroups([...newGroups]);
  };

  const handleIsTextableChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsTextable(event.target.checked);
  };

  const handleCancel = () => {
    setOpen(false);
    setUserNotifGroups([]);
    setIsTextable(userInfoStore.userNotificationPrefs.isTextable);
    setSaveDisabled(true);
  };

  const handleSave = () => {
    dispatch(updateUserNotificationPrefsRequest(userNotifGroups, userInfoStore.userInfo.email ? userInfoStore.userInfo.email : "", userIsTextable))
    setOpen(false);
    setUserNotifGroups([]);
    setSaveDisabled(true);
  };

  return (
    <>
      <Tooltip title="Edit Notification Preferences within the VCC">
        <Box>
          <Button
            variant="contained"
            color="inherit"
            onClick={handleOpen}
            style={{ minWidth: "150px", color: "black" }}
          >
            Edit Notification Preferences
          </Button>
        </Box>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleCancel}
        fullWidth
        maxWidth="md"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Edit Notification Preferences</DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item p={1} xs={12} sm={6}>
              <FormGroup>
                <Typography variant="h5" component="h2">Notify me about</Typography>
                <Typography variant="h6" component="h3">Activities</Typography>
                <Grid container>
                  {
                    userInfoStore.allNotificationGroups
                      .filter((g) => !g.name.includes("JOG") && !g.name.includes("GROUP") && g.name !== "VCC Incident Created")
                      .sort((g1, g2) => g1.name > g2.name ? 1 : -1)
                      .map((notifGroup: NotificationGroup) => (
                        <Grid key={notifGroup.id} item xs={12}>
                          <FormControlLabel key={notifGroup.id} control={
                            <Checkbox
                              checked={userNotifGroups.includes(Number(notifGroup.id))}
                              id={notifGroup.id}
                              onChange={handleNotifGroupChange}
                            />
                          }
                            label={notifGroup.name}
                          />
                        </Grid>
                      ))
                  }
                </Grid>
              </FormGroup>
              <Typography variant="h6" component="h3">VCC Incident Created</Typography>
              <Grid container>
                {
                  userInfoStore.allNotificationGroups
                    .filter((g) => g.name.includes("JOG") || g.name.includes("GROUP"))
                    .sort((g1, g2) => g1.name > g2.name ? 1 : -1)
                    .map((notifGroup: NotificationGroup) => (
                      <Grid key={notifGroup.id} item xs={12}>
                        <FormControlLabel control={
                          <Checkbox
                            checked={userNotifGroups.includes(Number(notifGroup.id))}
                            id={notifGroup.id}
                            onChange={handleNotifGroupChange}
                          />
                        }
                          label={notifGroup.name}
                        />
                      </Grid>
                    ))
                }
              </Grid>
            </Grid>
            <Grid item p={1} xs={12} sm={6}>
              <FormGroup>
                <Typography variant="h5" component="h2">Send Notifications Via</Typography>
                <Grid container>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox id={NotifMediums.EMAIL} checked={true} disabled={true} />
                      }
                      label="Email"
                    />
                    <Tooltip title="Always enabled">
                      <InfoIcon />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          id={NotifMediums.PHONE}
                          checked={userIsTextable}
                          onChange={handleIsTextableChange}
                          disabled={!userInfoStore.userInfo.phoneNumber}
                        />
                      }
                      label={
                        "Text message" + (
                          userInfoStore.userInfo.phoneNumber
                            ? " to: " + ParsePhoneNumberUtil(userInfoStore.userInfo.phoneNumber)
                            : "")
                      }
                    />
                    <Tooltip title={userInfoStore.userInfo.phoneNumber
                      ? "Ensure this is a textable number. Otherwise, edit your profile phone number to use this feature."
                      : "Add a number to your profile to enable this option."}>
                      <InfoIcon />
                    </Tooltip>
                  </Grid>
                </Grid>
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ margin: "1em" }}>
          <Button onClick={handleCancel} color="inherit" variant="contained">
            Cancel
          </Button>
          <Button
            disabled={saveDisabled}
            onClick={handleSave}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditNotificationPreferences;
